document.addEventListener('DOMContentLoaded', () => {

    /* ── Utility: Strict Image Loader ── */
    function imagesLoaded(root) {
        const imgs = root.querySelectorAll('img');
        const promises = Array.from(imgs, img => {
            if (img.complete) return Promise.resolve();
            return new Promise(resolve => {
                img.addEventListener('load', () => resolve(), { once: true });
                img.addEventListener('error', () => resolve(), { once: true });
            });
        });
        return Promise.all(promises);
    }

    /* ── Core Logic: Measure & Apply ── */
    function equalizeHeights(tabsComponent) {
        const currentTab = tabsComponent.querySelector('.tab.current');
        if (!currentTab) return;

        const container = currentTab.querySelector('.tab-collections__container');
        if (!container) return;

        const items = container.querySelectorAll('.product-grid-item');

        // TARGETING UPDATE: Only select the DESKTOP featured item.
        // The mobile one is outside and should resize naturally.
        const featuredInner = container.querySelector('.featured-item--desktop .tab-collections__featured-item');
        const infos = container.querySelectorAll('.product-grid-item__info');

        if (items.length === 0) return;

        // 1. Reset heights
        items.forEach(item => {
            if (!item.classList.contains('product-grid-item--featured')) {
                item.style.height = 'auto';
            }
        });
        if (featuredInner) featuredInner.style.height = 'auto';
        infos.forEach(info => info.style.height = 'auto');

        // 2. Measure
        let maxCardHeight = 0;
        items.forEach(item => {
            // Skip featured items for measurement
            if (item.classList.contains('product-grid-item--featured')) return;
            // Skip hidden mobile items (offsetParent is null when hidden)
            if (item.offsetParent === null) return;

            maxCardHeight = Math.max(maxCardHeight, item.offsetHeight);
        });

        let maxInfoHeight = 0;
        infos.forEach(info => {
            maxInfoHeight = Math.max(maxInfoHeight, info.offsetHeight);
        });

        // 3. Apply
        if (maxCardHeight > 0) {
            items.forEach(item => {
                if (item.classList.contains('product-grid-item--featured')) return;
                item.style.height = maxCardHeight + 'px';
            });

            // Apply only to desktop featured item
            if (featuredInner) {
                featuredInner.style.height = maxCardHeight + 'px';
            }
        }

        if (maxInfoHeight > 0) {
            infos.forEach(info => info.style.height = maxInfoHeight + 'px');
        }
    }

    /* ── Scrollbar Logic ── */
    function updateScrollbar(tabsComponent) {
        // 1. Find the Active Tab first
        const currentTab = tabsComponent.querySelector('.tab.current');
        if (!currentTab) return;

        // 2. Find the fill element INSIDE the active tab
        // (This fixes the issue where it was finding the first tab's bar always)
        const fill = currentTab.querySelector('[data-tab-progress-fill]');
        if (!fill) return;

        const carousel = currentTab.querySelector('grid-slider');
        if (!carousel) return;

        // 3. Calculate based on VISIBLE items only
        const allItems = carousel.querySelectorAll('.product-grid-item');
        const visibleItemsCount = Array.from(allItems).filter(el => el.offsetParent !== null).length || 1;
        const thumbPct = (1 / visibleItemsCount) * 100;
        fill.style.width = thumbPct + '%';

        let ratio = 0;
        const flkty = carousel.flkty || (window.Flickity && Flickity.data(carousel));

        // Check if Flickity is active (Desktop)
        if (flkty && flkty.isActive) {
            const lastSlide = flkty.slides[flkty.slides.length - 1];
            const maxTravel = lastSlide ? Math.abs(lastSlide.target) : 0;
            const currentPos = Math.abs(flkty.x);
            ratio = maxTravel > 0 ? Math.min(currentPos / maxTravel, 1) : 0;
        }
        // Native Scroll Logic (Mobile)
        else {
            const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
            if (maxScrollLeft > 0) {
                ratio = carousel.scrollLeft / maxScrollLeft;
                ratio = Math.max(0, Math.min(ratio, 1));
            }
        }

        fill.style.left = (ratio * (100 - thumbPct)) + '%';
    }

    /* ── Initialization ── */
    function initTabComponent(tabsComponent) {
        const resizeObserver = new ResizeObserver(entries => {
            window.requestAnimationFrame(() => {
                equalizeHeights(tabsComponent);
                updateScrollbar(tabsComponent);
            });
        });

        const container = tabsComponent.querySelector('.tab-collections__container');
        if (container) resizeObserver.observe(container);

        const infos = tabsComponent.querySelectorAll('.product-grid-item__info');
        infos.forEach(info => resizeObserver.observe(info));

        const carousels = tabsComponent.querySelectorAll('grid-slider');
        carousels.forEach(carousel => {

            // Native Scroll Listener
            carousel.addEventListener('scroll', () => {
                window.requestAnimationFrame(() => updateScrollbar(tabsComponent));
            }, { passive: true });

            // Flickity Listener
            const onFlickityReady = () => {
                const flkty = carousel.flkty || (window.Flickity && Flickity.data(carousel));
                if (flkty) {
                    flkty.on('scroll', () => window.requestAnimationFrame(() => updateScrollbar(tabsComponent)));
                    equalizeHeights(tabsComponent);
                }
            };

            if (carousel.classList.contains('flickity-enabled')) {
                onFlickityReady();
            } else {
                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.target.classList.contains('flickity-enabled')) {
                            observer.disconnect();
                            onFlickityReady();
                        }
                    });
                });
                observer.observe(carousel, { attributes: true, attributeFilter: ['class'] });
            }
        });
    }

    /* ── Main Execution ── */
    const allTabsComponents = document.querySelectorAll('tabs-component[data-tabs-holder]');

    allTabsComponents.forEach(initTabComponent);

    allTabsComponents.forEach(tabsComponent => {
        const tabsNav = tabsComponent.querySelector('.tabs__nav');
        if (tabsNav) {
            new MutationObserver(() => {
                window.requestAnimationFrame(() => {
                    equalizeHeights(tabsComponent);
                    updateScrollbar(tabsComponent);
                });
            }).observe(tabsNav, { attributes: true, attributeFilter: ['class'], subtree: true });
        }
    });

    window.addEventListener('load', () => {
        allTabsComponents.forEach(c => {
            equalizeHeights(c);
            updateScrollbar(c);
        });
    });

    // Shopify.designMode is the runtime equivalent of Liquid's request.design_mode
    if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
            const tabsComponent = event.target.closest('tabs-component');
            if (tabsComponent) {
                setTimeout(() => equalizeHeights(tabsComponent), 300);
            }
        });
    }
});
